import React, { useState } from "react";
import { motion } from "framer-motion";

const PrivacyPolicy = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const policySections = [
    {
      id: "data-collection",
      title: "1. Information We Collect",
      content: (
        <>
          <p className="mb-4">
            We collect the following information to provide and improve our
            services:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Personal Information:</strong> Name, email address, phone
              number, and your address when you place an order or create an
              account.
            </li>
            <li>
              <strong>Payment Information:</strong> Credit card details are
              processed securely through our payment services - we never store
              full payment details on our servers.
            </li>
            <li>
              <strong>Design Preferences:</strong> Window measurements, fabric
              choices, and customization requests for your curtain orders.
            </li>
            <li>
              <strong>Website Usage:</strong> Cookies, IP address, browser type,
              and pages visited to improve your shopping experience (via tools
              like Google Analytics).
            </li>
          </ul>
        </>
      ),
    },
    {
      id: "data-use",
      title: "2. How We Use Your Information",
      content: (
        <>
          <p className="mb-4">
            Your information helps us provide excellent service:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Process and fulfill your curtain orders, including custom designs
            </li>
            <li>
              Communicate about orders, shipping, and installation services
            </li>
            <li>Respond to your inquiries and provide customer support</li>
            <li>Improve our products, website, and showroom experiences</li>
            <li>Send promotional offers (only with your explicit consent)</li>
          </ul>
        </>
      ),
    },
    {
      id: "data-sharing",
      title: "3. Information Sharing",
      content: (
        <>
          <p className="mb-4">
            We value your privacy and only share data when necessary:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Shipping Partners:</strong> Delivery information shared
              with carriers to deliver your curtains with your measurements
            </li>
            <li>
              <strong>Payment Processors:</strong> Secure transaction processing
            </li>
            <li>
              <strong>Installation Services:</strong> If you use our
              professional installation, we share necessary details with our
              certified installers
            </li>
            <li>
              <strong>Legal Requirements:</strong> When required by law or to
              protect our rights
            </li>
          </ul>
          <p className="mt-4">
            We <strong>never</strong> sell your personal information to third
            parties.
          </p>
        </>
      ),
    },
    {
      id: "security",
      title: "4. Data Security",
      content: (
        <>
          <p className="mb-4">We protect your information with:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>SSL encryption on all data transmissions</li>
            <li>Secure storage of sensitive information</li>
            <li>Regular security audits and updates</li>
            <li>Limited employee access to customer data</li>
          </ul>
        </>
      ),
    },
    {
      id: "your-rights",
      title: "5. Your Privacy Rights",
      content: (
        <>
          <p className="mb-4">You have the right to:</p>
          <ul className="list-disc pl-6 space-y-2 text-sm text-gray-700">
            <li>
              You can access, correct, or delete your personal data at any time.
            </li>
            <li>
              You may opt out of marketing communications —
              <a
                href="mailto:sahlacurtaincenter786@gmail.com?subject=Unsubscribe"
                className="text-blue-500 hover:underline ml-1"
              >
                unsubscribe here
              </a>
              .
            </li>
            <li>You can request a copy of your data in a portable format.</li>
            <li>
            Delete your account (except transaction records required by law)
            </li>
          </ul>

          <p className="mt-4">
            To exercise these rights, please contact us at{" "}
            <a
              href="mailto:sahlacurtaincenter786@gmail.com"
              className="text-blue-400 hover:underline"
            >
              sahlacurtaincenter786@gmail.com
            </a>
            .
          </p>
        </>
      ),
    },
    {
      id: "cookies",
      title: "6. Cookies & Tracking",
      content: (
        <>
          <p className="mb-4">We use cookies to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Remember your design preferences and shopping cart</li>
            <li>Analyze website traffic and improve performance</li>
            <li>
              Provide personalized recommendations based on your style
              preferences
            </li>
          </ul>
          <p className="mt-4">
            You can manage cookies through your browser settings, though some
            features may not work properly without them.
          </p>
        </>
      ),
    },
    {
      id: "changes",
      title: "7. Policy Updates",
      content: (
        <p>
          We may update this policy occasionally. Significant changes will be
          notified through our website or email. The "Last Updated" date at the
          top will reflect the most recent version.
        </p>
      ),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <motion.h1
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl font-bold text-gray-800 mb-4"
          >
            Privacy Policy
          </motion.h1>
          <p className="text-gray-600">
            Last Updated:{" "}
            {new Date().toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>

        <div className="space-y-4">
          {policySections.map((section) => (
            <motion.div
              key={section.id}
              whileHover={{ x: 5 }}
              className="border border-gray-200 rounded-lg overflow-hidden shadow-sm"
            >
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full text-left p-6 bg-gray-800 hover:bg-gray-700 transition-colors duration-200"
              >
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-white">
                    {section.title}
                  </h2>
                  <svg
                    className={`w-5 h-5 text-white transform transition-transform duration-200 ${
                      expandedSection === section.id ? "rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </button>
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{
                  opacity: expandedSection === section.id ? 1 : 0,
                  height: expandedSection === section.id ? "auto" : 0,
                }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="p-6 bg-white text-gray-600">
                  {section.content}
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        <div className="bg-gray-100 p-6 rounded-lg border border-gray-200 mt-9">
          <h3 className="text-xl font-semibold text-gray-800 mb-3">
            Contact Us
          </h3>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="mr-2">📧</span>
              <a
                href="mailto:sahlacurtaincenter786@gmail.com"
                className="text-blue-600 hover:underline"
              >
                sahlacurtaincenter786@gmail.com
              </a>
            </li>
            <li className="flex items-start">
              <span className="mr-2">📞</span>
              <a
                href="tel:+919895846494"
                className="text-blue-600 hover:underline"
              >
                +91 98958 46494
              </a>
            </li>
            <li className="flex items-start">
              <span className="mr-2">🏠</span>
              <span>
                Mambara Rd, Kadungathukundu Town, Kalpakanchery, Kerala 676551
              </span>
            </li>
          </ul>
          <p className="mt-4 text-gray-600">
            For data access requests, please email us with "Privacy Request" in
            the subject line.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default PrivacyPolicy;
